import {Injectable} from "@angular/core";
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {exhaustMap, map, Observable, of, switchMap, withLatestFrom} from 'rxjs';
import {SavedReportsService} from "../../services/saved-reports.service";
import {Serializer, SerializerResponse} from '../../interfaces/serializer';
import {SavedReport} from "../../interfaces/saved-reports";
import * as savedReports from "./savedReports.actions";
import {ReportsRequestsService} from "../../services/reports-requests.service";
import {showSnackbar} from "../snackbar/snackbar.actions";
import {
  createSavedReportsSuccess,
  initializeSavedReports,
  initializeSavedReportsSuccess,
  loadSavedReportsSuccess,
  updateSavedReportsStandaloneSuccess,
  updateSavedReportsSuccess
} from './savedReports.actions';
import {Store} from "@ngrx/store";
import {
  selectSavedReportComponentObjects,
  selectSavedReportsLoaded
} from './savedReports.selectors';
import {filter} from 'rxjs/operators';
import {selectUserComponentObjects, selectUsersLoaded} from '../users/users.selectors';
import {UserComponentObjects} from '../../classes/user-component-object';
import {SavedReportComponentObjects} from '../../classes/saved-report-component-object';
import {loadUsersSuccess} from '../users/users.actions';
import {selectFolderComponentObjects, selectReportsFoldersLoaded} from '../savedReportsFolders/savedReportsFolders.selectors';
import {FolderComponentObjects} from '../../classes/folder-component-object';
import {deleteSavedReportsFolderSuccess, loadSavedReportsFoldersSuccess} from '../savedReportsFolders/savedReportsFolders.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {Site} from '../../interfaces/site';
import {selectSite} from '../init/init.selectors';

@Injectable()
export class SavedReportsEffects {
  private readonly _dataSet$: Observable<Serializer<Site>> = this._store.select(selectSite);

  private readonly _load$ = createEffect(() => this._actions$.pipe(
    ofType(savedReports.loadSavedReports),
    switchMap((action: TypedAction<string> & { parameters: boolean }) => this._savedReportS.getSavedReports(action.parameters).pipe(
      map((response) => savedReports.loadSavedReportsSuccess({
        savedReports: response.data,
        included: response.included
      }))
    ))
  ));

  private readonly _create$ = createEffect(() => this._actions$.pipe(
    ofType(savedReports.createSavedReports),
    withLatestFrom(this._dataSet$),
    exhaustMap(([action, dataSet]) => this._adReportsRequestS.saveReport(
      dataSet,
      action.report,
      action.params,
      action.options,
      action.limit
    ).pipe(
      switchMap((response: SerializerResponse<Serializer<SavedReport>>): Array<TypedAction<any>> => [
        savedReports.createSavedReportsSuccess({
          report: response.data,
          exit: action.exit
        }),
        showSnackbar({
          message: 'snackbar.save_done.saved',
          icon: 'save'
        })
      ])
    ))
  ));

  private readonly _update$ = createEffect(() => this._actions$.pipe(
    ofType(savedReports.updateSavedReports),
    withLatestFrom(this._dataSet$),
    exhaustMap(([action, dataSet]) => this._adReportsRequestS.updateReport(
      dataSet,
      action.report,
      action.update,
      action.params,
      action.options,
      action.limit
    ).pipe(
      switchMap((response) => [
        savedReports.updateSavedReportsSuccess({
          report: response.data,
          exit: action.exit
        }),
        showSnackbar({
          message: 'snackbar.save_done.saved',
          icon: 'save'
        })
      ])
    ))
  ));

  private readonly _public$ = createEffect(() => this._actions$.pipe(
    ofType(savedReports.shareWithLinkSavedReports),
    exhaustMap((action) => this._savedReportS.public(action.report, action.isPublic, action.password).pipe(
      switchMap((response) => [
        savedReports.updateSavedReportsSuccess({
          report: response.data,
          exit: false
        }),
        showSnackbar({
          message: 'snackbar.save_done.saved',
          icon: 'save'
        })
      ])
    ))
  ));

  private readonly _favorite$: Observable<TypedAction<string> & { report: Serializer<SavedReport> }> & CreateEffectMetadata = createEffect(() => this._actions$.pipe(
    ofType(savedReports.favoriteSavedReport),
    switchMap((action: TypedAction<string> & { report: Serializer<SavedReport>, is_favorite: boolean }): Observable<SerializerResponse<Serializer<SavedReport>>> => this._savedReportS.favorite(action.report, action.is_favorite)),
    map((response: SerializerResponse<Serializer<SavedReport>>): TypedAction<string> & { report: Serializer<SavedReport> } => savedReports.favoriteSavedReportSuccess({ report: response.data }))
  ));

  private readonly _createStandalone$ = createEffect(() => this._actions$.pipe(
    ofType(savedReports.createSavedReportsStandalone),
    exhaustMap((action) => this._adReportsRequestS.createReportStandalone(null, null, action.report).pipe(
      switchMap((response) => [
        savedReports.createSavedReportsStandaloneSuccess({
          report: response.data
        }),
        showSnackbar({
          message: 'snackbar.save_done.saved',
          icon: 'save'
        })
      ])
    ))
  ));

  private readonly _updateStandalone$ = createEffect(() => this._actions$.pipe(
    ofType(savedReports.updateSavedReportsStandalone),
    withLatestFrom(this._dataSet$),
    exhaustMap(([action, dataSet]) => this._adReportsRequestS.updateReportStandalone(dataSet, action.report, action.update).pipe(
      switchMap((response) => [
        savedReports.updateSavedReportsStandaloneSuccess({
          report: response.data
        }),
        showSnackbar({
          message: 'snackbar.save_done.saved',
          icon: 'save'
        })
      ])
    ))
  ));

  private readonly _delete$ = createEffect(() => this._actions$.pipe(
    ofType(savedReports.deleteSavedReports),
    exhaustMap((action) => this._savedReportS.deleteReport(action.report).pipe(exhaustMap(() => {
        return of(savedReports.deleteSavedReportsSuccess({
          report: action.report
        }));
      }))
    )));

  // Favorites


  // ############# INIT #############
  private readonly _canInitReports: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(
      loadSavedReportsSuccess,
      createSavedReportsSuccess,
      updateSavedReportsSuccess,
      updateSavedReportsStandaloneSuccess,
      loadUsersSuccess,
      loadSavedReportsFoldersSuccess,
      deleteSavedReportsFolderSuccess
    ),
    withLatestFrom(
      this._store.select(selectSavedReportsLoaded),
      this._store.select(selectUsersLoaded),
      this._store.select(selectReportsFoldersLoaded)
    ),
    filter(([_, savedReportsLoaded, userLoaded, foldersLoaded]: [TypedAction<any>, boolean, boolean, boolean]): boolean => savedReportsLoaded && userLoaded && foldersLoaded),
    map(() => initializeSavedReports())
  ));

  private readonly _initReports: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(initializeSavedReports),
    withLatestFrom(
      this._store.select(selectSavedReportComponentObjects),
      this._store.select(selectUserComponentObjects),
      this._store.select(selectFolderComponentObjects)
    ),
    map(([_, savedReports, users, folders]: [TypedAction<string>, SavedReportComponentObjects, UserComponentObjects, FolderComponentObjects]) => {
      for (const savedReport of savedReports) {
        savedReport.init(users, folders);
      }

      return initializeSavedReportsSuccess();
    })
  ));

  constructor(
    private readonly _actions$: Actions,
    private readonly _savedReportS: SavedReportsService,
    private readonly _adReportsRequestS: ReportsRequestsService,
    private readonly _store: Store
  ) {
  }

}
